import { ProxyAction } from "../Custom Hooks/useProxyAction";
import { ChangeEvent, KeyboardEvent, useCallback } from "react";
import { Flex, Stack } from "@chakra-ui/react";
import Title from "../Common/Title";
import { Typography } from "@sgi/gravity/Typography";
import { ErrorMessage } from "@sgi/gravity/ErrorMessage";
import { Input } from "@sgi/gravity/Input";
import { Button } from "@sgi/gravity/Button";
import { Link } from "@sgi/gravity/Link";
import CancelButton from "../Common/Form/CancelButton";

export type PhoneFactorTextMeProps = {
  readonly lastTwoDigits: string;
  readonly pageError: string;
  readonly verificationCodeError: string;
  readonly verificationCode: string;
  readonly verifyCodeAction: ProxyAction;
  readonly sendANewCodeAction: ProxyAction;
  readonly setVerificationCode: (value: string) => void;
}

export default function PhoneFactorTextMe({
  lastTwoDigits,
  pageError,
  verificationCodeError,
  verificationCode,
  verifyCodeAction,
  sendANewCodeAction,
  setVerificationCode,
}: PhoneFactorTextMeProps) {
  const onVerifyChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setVerificationCode(e.target.value),
    [setVerificationCode]
  );
  const onVerifyKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") verifyCodeAction.act();
    },
    [verifyCodeAction]
  );
  const handleSendNewCodeAction = useCallback(() => {
    setVerificationCode("");
    sendANewCodeAction.act();
  }, [setVerificationCode, sendANewCodeAction]);

  return (
    <Flex
      direction="column"
      maxW={570}
      padding={38}
      mx="auto"
      justifyContent="center"
      data-testid="phone-factor-text-me"
    >
      <Title text="Enter verification code"/>
      <Typography>
        Your one-time verification code has been sent by <strong>text message</strong> to
      </Typography>
      <Typography variant="bodyBold">
        +1 (•••) ••• - ••{lastTwoDigits}
      </Typography>
      <Flex
        direction="column"
        maxW={600}
        padding={25}
        mx="auto"
        data-testid="confirm-phone-factor-test"
      >
        {pageError && <ErrorMessage>{pageError}</ErrorMessage>}
        <Input
          controlClassName="my-3"
          label="Verification code"
          name="Verification"
          id="verify-code-field"
          type="text"
          style={{backgroundColor: "white"}}
          onChange={onVerifyChange}
          onKeyDown={onVerifyKeyDown}
          placeholder="Enter verification code here"
          error={verificationCodeError}
          data-testid="code-input-test"
          isRequired
          value={verificationCode}
          maxLength={6}
          pattern="\\d{6}"
          autoFocus
          my={2}
        />
        <Button
          isFullWidth
          data-testid="verify-code-button-test"
          onClick={verifyCodeAction.act}
          isDisabled={verifyCodeAction.disabled}
          my={3}
        >
          Verify code
        </Button>
        <CancelButton data-testid="cancel-button-test" my={1} mt={2} />
        <Stack direction="row">
          <Typography variant="bodyBold" color="gray.800">
            Didn't receive the text message?
          </Typography>
          <Link
            href="#"
            data-testid="send-new-code-button-test"
            onClick={sendANewCodeAction.disabled ? undefined : handleSendNewCodeAction}
            my={1}
          >
            Resend Code
          </Link>
        </Stack>
      </Flex>
    </Flex>
  );
}
